import ArrowLeft from '@assets/icons/arrow-left.svg';
import CatalogEmptyState from '@assets/icons/catalog-empty-state.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  ALL_CATEGORIES_FILTER,
  ALL_CATEGORY_VALUE,
  NULL_CATEGORY_VALUE,
} from '@/utils/category';
import { CATALOG_ROUTES, ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import { CatalogCarouselsLoading } from '@/components/catalog-search/catalog-carousels-loading';
import { CatalogCategoryList } from '@/components/catalog-search/catalog-category-list';
import {
  CatalogCategoryType,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { CatalogItemListLoading } from '@/components/catalog-search/catalog-item-list-loading';
import { CatalogSidebarLoading } from '@/components/catalog-search/catalog-sidebar-loading';
import { CatalogStackParams, OrderGuideStackParams } from '@/navigators/types';
import { CategoryCarousels } from '@/components/catalog-search/catalog-carousels';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { FilterOption, SplitFilterList } from '@/components/split-filter-list';
import { FulfillmentAvailability } from '@/components/partners/fulfillment-availability';
import { getActiveRoute } from '@/utils/navigation';
import { HorizontalSelector } from '@/components/horizontal-selector';
import { ItemSearchHeaderAction } from '@/components/item-search-header-action';
import { OfflineComponent } from '@/components/offline-component';
import { PageContainer } from '@/components/page-container';
import { SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { ScrollViewProps, Text, View } from 'react-native';
import { SearchCatalogContent } from '@/components/search-catalog-content';
import { SearchEntryPoint } from './types';
import { SearchFilterPills } from '@/components/search-filters/search-filter-pills';
import { SearchNoteActionsProvider } from '@/contexts/search-note-actions-context';
import { SearchNotePartitionSheet } from '@/components/order-guide/search-note-partition-sheet';
import { SplitFilterContainer } from '@/components/split-filter-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { UnifiedSearchEmptyState } from '@/components/order-guide/unified-search-empty-state';
import { useCatalogCategoryItems } from '@/hooks/use-catalog-category-items';
import { useCatalogCategoryListsEnabled } from '@/hooks/dynamic-config/use-catalog-category-lists-enabled';
import { useCategoryCountsMap } from '@/hooks/use-category-counts-map';
import { useConsolidatedCatalogFilters } from '@/hooks/catalog-search/use-consolidated-catalog-filters';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useEscapeKeyClose } from '@platform/shortcuts/hooks/use-escape-key-close';
import { useFilteredCategoryCatalog } from '@/hooks/catalog-search/use-filtered-category-catalog';
import { useFilteredSearchCatalog } from '@/hooks/catalog-search/use-filtered-search-catalog';
import { useFocusEffect } from '@react-navigation/native';
import { useGeneratedCategories } from '@/hooks/use-generated-categories';
import { useHeaderRestaurantSelectorProps } from '@/components/use-header-restaurant-selector-props';
import { useItemGroupBadgeFilters } from '@/hooks/use-item-group-badge-filters';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useSearchOrderGuideActions } from '@/hooks/use-search-order-guide-actions';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackGroupItems } from '@/hooks/use-catalog-variant-pack-group-items';

interface Props {
  category?: CatalogCategoryType;
  navigation: Navigation;
  searchEntryPoint?: SearchEntryPoint;
}

type Navigation =
  | StackNavigationProp<
      OrderGuideStackParams,
      ORDER_GUIDE_ROUTES.OrderGuideUnifiedSearch
    >
  | StackNavigationProp<
      CatalogStackParams,
      CATALOG_ROUTES.CatalogUnifiedSearch
    >;

export const UnifiedSearchDesktop: React.FC<Props> = ({
  category,
  navigation,
  searchEntryPoint,
}) => {
  const baseNavHeaderProps = useHeaderRestaurantSelectorProps();
  const { customOrderGuideEditingEnabled } = useNewOrderContext();

  const [styles, theme] = useStyles();
  const preserveStackRef = useRef(false);

  const { isGuestMode } = useUserContext();
  const isOfflineModeActive = useOfflineModeActive();

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');

  const [selectedCategory, setSelectedCategory] = useState<
    CatalogCategoryType | undefined
  >(category);
  const [searchText, setSearchText] = useState<string | null>(null);

  const { categoryItemsList, hasResponse: categoryItemsHasResponse } =
    useCatalogCategoryItems();
  const { variantPackGroupItemsList, hasResponse: groupItemsHasResponse } =
    useVariantPackGroupItems();

  const searchCatalog = useFilteredSearchCatalog({
    category: selectedCategory,
    searchEntryPoint,
  });
  const categoryCatalog = useFilteredCategoryCatalog({
    category: selectedCategory,
  });
  const { badges } = useItemGroupBadgeFilters({
    category: selectedCategory,
    items: categoryCatalog.listData,
  });

  const orderGuideUnifiedSearchEnabled = useSupplierDynamicConfigValue(
    'order_guide_unified_search_enabled',
  );
  const catalogCategoryListsEnabled = useCatalogCategoryListsEnabled();

  const {
    addNoteState,
    editSearchItemNotes,
    handleAddNotePress,
    setAddNoteState,
  } = useSearchOrderGuideActions({});

  const wrappedHandleAddNotePress = useCallback(
    (itemMetadata: ItemListOptionMetadata) => {
      // without this, closing the action sheet will go to previous screen
      // (needs to be set here because handleAddNotePress __might__ go straight to editSearchItemNotes)
      preserveStackRef.current = true;

      handleAddNotePress(itemMetadata);
    },
    [handleAddNotePress],
  );

  const searchOrderGuideActionsEnabled =
    orderGuideUnifiedSearchEnabled && customOrderGuideEditingEnabled;
  const {
    changeSortOrder,
    clearSearch,
    items,
    searchFilters,
    searchItems,
    searchResults,
    searchSortOrder,
    queryLoading,
  } = searchCatalog;

  const showSearchView =
    Boolean(searchText) || Boolean(searchFilters.selected.length);

  const catalogFilters = useConsolidatedCatalogFilters({
    badges,
    catalogCategoryListsEnabled,
    categoryCatalog,
    searchCatalog,
    showSearchView,
  });

  const hasResponse = itemGroupsEnabled
    ? groupItemsHasResponse
    : categoryItemsHasResponse;

  const { categories } = useGeneratedCategories();

  const categoryCountsMap = useCategoryCountsMap({
    categories,
    searchResults: searchResults ?? [],
  });

  useEscapeKeyClose(navigation.goBack);

  useFocusEffect(
    useCallback(() => {
      preserveStackRef.current = false;
      return () => {
        // without this, closing the action sheet will go to previous screen
        // (this gets set to true when the action sheet is opened below in wrappedHandleAddNotePress)
        if (preserveStackRef.current) {
          preserveStackRef.current = false;
          return;
        }

        const parent = navigation.getParent?.() ?? navigation;
        const nextState = parent.getState();
        const nextRoute = getActiveRoute(nextState);

        const goingToBrandPage =
          nextRoute?.name === ORDER_GUIDE_ROUTES.BrandPage ||
          nextRoute?.name === CATALOG_ROUTES.BrandPage;

        if (!goingToBrandPage) {
          navigation.popToTop();
        }
      };
    }, [navigation]),
  );

  const filterItems = useMemo<FilterOption[]>(() => {
    if (!hasResponse) {
      return [];
    }

    const filters: FilterOption[] = [];

    filters.push({
      metadata: {
        action: () => {
          setSelectedCategory(undefined);
        },
        ...(showSearchView && {
          count: categoryCountsMap.get(ALL_CATEGORY_VALUE),
        }),
        isSelected: !selectedCategory,
      },
      title: ALL_CATEGORIES_FILTER,
      value: ALL_CATEGORY_VALUE,
    });

    if (itemGroupsEnabled) {
      for (const { category } of variantPackGroupItemsList) {
        filters.push({
          metadata: {
            action: () => {
              setSelectedCategory(category);
            },
            ...(showSearchView && {
              count: category.uuid ? categoryCountsMap.get(category.uuid) : 0,
            }),
            isSelected: category.uuid === selectedCategory?.uuid,
          },
          title: category.name || i18n.t('other'),
          value: category.uuid || NULL_CATEGORY_VALUE,
        });
      }
    } else {
      for (const { category } of categoryItemsList) {
        filters.push({
          metadata: {
            action: () => {
              setSelectedCategory(category);
            },
            ...(showSearchView && {
              count: categoryCountsMap.get(
                category.name ?? NULL_CATEGORY_VALUE,
              ),
            }),
            isSelected: category.uuid === selectedCategory?.uuid,
          },
          title: category.name || i18n.t('other'),
          value: category.name || NULL_CATEGORY_VALUE,
        });
      }
    }

    return filters;
  }, [
    categoryCountsMap,
    categoryItemsList,
    hasResponse,
    itemGroupsEnabled,
    selectedCategory,
    showSearchView,
    variantPackGroupItemsList,
  ]);

  const sections = useMemo(
    () => [
      {
        data: itemGroupsEnabled ? variantPackGroupItemsList : categoryItemsList,
        key: null,
      },
    ],
    [categoryItemsList, itemGroupsEnabled, variantPackGroupItemsList],
  );

  const sideBarVisible = Boolean(filterItems.length > 1);

  const pageHeaderContainerProps = useMemo(() => {
    return {
      navigationHeaderProps: {
        ...baseNavHeaderProps,
        actions: [],
        animate: false,
        BackButtonIcon: ArrowLeft,
        centerTitle: false,
        contentFooter:
          isGuestMode || !searchText ? null : <FulfillmentAvailability />,
        responsive: true,
        showBackButton: true,
        subheader: (
          <>
            {sideBarVisible ? null : (
              <>
                <View style={styles.categoryBorder}>
                  <HorizontalSelector<FilterOption>
                    data={filterItems}
                    keyExtractor={(item) => String(item.value)}
                    onItemPress={(item) => {
                      item?.metadata!.action();
                    }}
                    selectedItem={
                      filterItems.find(
                        (i) =>
                          i.value ===
                          (selectedCategory?.uuid || ALL_CATEGORY_VALUE),
                      ) ?? null
                    }
                    trackingKey="catalog-screen-sections"
                  />
                </View>
                {selectedCategory &&
                selectedCategory.uuid !== ALL_CATEGORY_VALUE ? (
                  <SearchFilterPills
                    category={selectedCategory}
                    filters={catalogFilters}
                    onChangeSortOrder={changeSortOrder}
                    sortOrder={showSearchView ? searchSortOrder : undefined}
                  />
                ) : null}
              </>
            )}
          </>
        ),
        title: (
          <ItemSearchHeaderAction
            autoFocus
            containerStyle={styles.searchInputContainer}
            initialSearchText={searchText}
            onSubmit={async (value: string | null) => {
              setSearchText(value);
              if (value || Boolean(searchFilters.selected.length)) {
                await searchItems(value || '', searchSortOrder);
              } else {
                clearSearch();
              }
            }}
            placeholder={i18n.t('search')}
          />
        ),
      },
      statusBarColor: theme.colors.background,
    };
  }, [
    baseNavHeaderProps,
    catalogFilters,
    changeSortOrder,
    clearSearch,
    filterItems,
    sideBarVisible,
    isGuestMode,
    searchFilters,
    searchItems,
    searchSortOrder,
    showSearchView,
    selectedCategory,
    styles,
    theme,
    searchText,
  ]);

  // Tells ItemDetailActions to show note/essential item actions
  const searchNoteActionsValue = useMemo(
    () =>
      searchOrderGuideActionsEnabled
        ? { onNotePress: wrappedHandleAddNotePress }
        : null,
    [searchOrderGuideActionsEnabled, wrappedHandleAddNotePress],
  );

  const content = (scrollViewProps: ScrollViewProps) => {
    if (!searchText) {
      return <UnifiedSearchEmptyState />;
    }

    if (!hasResponse) {
      if (isOfflineModeActive) {
        return (
          <View
            style={[
              styles.staticContentContainer,
              sideBarVisible ? null : scrollViewProps.contentContainerStyle,
            ]}
          >
            <OfflineComponent />
          </View>
        );
      }

      return (
        <View
          style={[
            styles.staticContentContainer,
            sideBarVisible ? null : scrollViewProps.contentContainerStyle,
          ]}
          testID="loading-indicator"
        >
          {!selectedCategory && !searchText ? (
            <CatalogCarouselsLoading />
          ) : (
            <CatalogItemListLoading />
          )}
        </View>
      );
    }

    if (
      categoryItemsList.length === 0 &&
      variantPackGroupItemsList.length === 0
    ) {
      return (
        <EmptyStateMinimal
          containerStyle={
            sideBarVisible ? null : scrollViewProps.contentContainerStyle
          }
          ImageComponent={CatalogEmptyState}
          text={i18n.t('partner_supplier_catalog_empty_state.message')}
        />
      );
    }

    if (!selectedCategory && !searchText && !searchFilters.selected.length) {
      return (
        <CategoryCarousels
          contentContainerStyle={
            sideBarVisible ? null : scrollViewProps.contentContainerStyle
          }
          onSelectCategory={setSelectedCategory}
          scrollViewProps={scrollViewProps}
          sections={sections}
        />
      );
    }

    return (
      <>
        {sideBarVisible ? (
          <View style={styles.catalogFiltersWrapper}>
            <SearchFilterPills
              category={selectedCategory}
              filters={catalogFilters}
              onChangeSortOrder={changeSortOrder}
              sortOrder={showSearchView ? searchSortOrder : undefined}
            />
            {searchText ? (
              <View style={styles.searchText}>
                <Text style={TYPOGRAPHY.h2}>"{searchText}"</Text>
              </View>
            ) : null}
          </View>
        ) : null}
        {!showSearchView && selectedCategory ? (
          <CatalogCategoryList
            categoryCatalog={categoryCatalog}
            scrollViewProps={{ ...(!sideBarVisible && scrollViewProps) }}
          />
        ) : (
          <SearchCatalogContent
            items={items}
            loading={queryLoading}
            renderSource="search_supplier_catalog"
            scrollViewProps={{ ...(!sideBarVisible && scrollViewProps) }}
            searchText={searchText || ''}
          />
        )}
      </>
    );
  };

  return (
    <>
      <SearchNoteActionsProvider value={searchNoteActionsValue}>
        <PageContainer {...pageHeaderContainerProps} animateNavigationHeader>
          {(scrollViewProps) => (
            <SplitFilterContainer
              FilterComponent={
                !hasResponse ? (
                  <CatalogSidebarLoading />
                ) : (
                  searchText && (
                    <SplitFilterList
                      contentContainerStyle={styles.splitFilterContainer}
                      data={filterItems}
                      scrollEnabled
                    />
                  )
                )
              }
              filtersVisible={
                (!hasResponse || sideBarVisible) && Boolean(searchText)
              }
            >
              {content(scrollViewProps)}
            </SplitFilterContainer>
          )}
        </PageContainer>
      </SearchNoteActionsProvider>
      {addNoteState ? (
        <SearchNotePartitionSheet
          onClose={() => {
            setAddNoteState(null);
            preserveStackRef.current = false;
          }}
          onPartitionSelect={(partitionUUID) => {
            // without this, closing the action sheet will go to previous screen
            preserveStackRef.current = true;

            editSearchItemNotes(addNoteState.itemMetadata, partitionUUID);
          }}
          variantPackUUID={addNoteState.itemMetadata.variantPackUUID}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  catalogFiltersWrapper: {
    marginLeft: SCREEN_PADDING_SMALL,
  },
  categoryBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  searchInputContainer: {
    flex: 1,
  },
  searchText: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  splitFilterContainer: {
    marginTop: 8,
    paddingRight: 8,
  },
  staticContentContainer: {
    flex: 1,
    marginTop: 24,
  },
}));
